/**
 * Main application shell: responsive navigation drawer with notifications and per-permission menus.
 *
 * @module components/common/ResponsiveDrawer
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import AppBar from "@material-ui/core/AppBar";
import Box from "@material-ui/core/Box";
import CssBaseline from "@material-ui/core/CssBaseline";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";
import Hidden from "@material-ui/core/Hidden";
import IconButton from "@material-ui/core/IconButton";
import PlayIcon from "@material-ui/icons/PlayCircleFilled";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import DashboardIcon from "@material-ui/icons/Dashboard";
import AdminIcon from '../../assets/images/icons/Admin.png';
import SettingsIcon from "@material-ui/icons/Settings";
import ExpandMore from "@material-ui/icons/ExpandMore";
import ExpandLess from "@material-ui/icons/ExpandLess";
import MenuIcon from "@material-ui/icons/Menu";
import LiveHelpIcon from "@material-ui/icons/LiveHelp";
import Toolbar from "@material-ui/core/Toolbar";
import Popover from "@material-ui/core/Popover";
import TextareaAutosize from "@material-ui/core/TextareaAutosize";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import CheckIcon from "@material-ui/icons/Check";
import {makeStyles, useTheme, createTheme} from "@material-ui/core/styles";
import Draggable from "react-draggable";
import CloseIcon from "@material-ui/icons/Close";
import {TextField, ThemeProvider} from "@material-ui/core";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import {connect} from "react-redux";
import StyledLink from "./StyledLink";
import NewSimMenu from "./NewSimMenu.jsx";
import {
    userLogout,
    fetchNotification,
    resetNotification,
    resetNotifications
} from "../../redux/authentication/action";
import { notesSave, fetchNotes, deleteNotes, reset } from "../../redux/sims/actions";
import ResultScreen from "./ResultScreen.jsx";
import DragIcon from "../../assets/images/Dragicon.png";
import Notes from '../../assets/images/icons/Notes.png';
import iLearnLogo from '../../assets/images/login/logo_placeholder.gif';
import SimsPermissions from '../../permissions/sims';
import DashboardPermissions from '../../permissions/dashboard';
import AdminPermissions from '../../permissions/admin';
import HelpdeskPermissions from '../../permissions/helpdesk';
import {permissionCheck} from '../../utils/permissionCheck';
import ResponsiveDrawerStyle from './ResponsiveDrawer';
import DownloadButtons from './DownloadButtons';
import NotificationsIcon from '@material-ui/icons/Notifications';
import Badge from '@material-ui/core/Badge';
import GetAppIcon from "@material-ui/icons/GetApp";
import {downloadExcelNotes, fetchBranding} from "../../services/result.service";
import FileSaver from "file-saver";
import Popper from '@material-ui/core/Popper';
import HelpRequestButton from "../helpdesk/HelpRequestButton";
import AlertPopup from "./AlertPopup.jsx";

/**
 * Represents the custom theme configuration for a menu component.
 * The theme defines specific color styling for the palette and action elements.
 *
 * Properties:
 * - `palette.divider`: Specifies the color of the divider lines within the menu.
 * - `palette.action.selected`: Defines the background color for selected menu items.
 */
const menuTheme = createTheme({
    palette: {
        divider: 'rgb(51, 64, 88)',
        action: {
            selected: "#3061b9",
        },
    },
});

/**
 * ResponsiveDrawer is a React functional component that serves as the main
 * layout for navigation and application-level features in a responsive UI.
 * It manages states and behaviors associated with user navigation, access
 * permissions, notifications, notes, helpdesk integration, and more.
 *
 * @param {Object[]} allNotes - List of all notes available to the user.
 * @param {string} title - Title of the current page, displayed on the browser tab.
 * @param {string} subtitle - Subtitle providing additional context for the current page.
 * @param {React.ReactNode} children - Content to be rendered within the drawer's main area.
 * @param {Function} userLogout - Function to log the user out of the application.
 * @param {Function} notesSave - Function to save notes created or edited by the user.
 * @param {Function} fetchNotes - Function to fetch user notes from the backend.
 * @param {Function} deleteNotes - Function to delete a specific user note.
 * @param {boolean} showResultButtons - Controls the visibility of result-related buttons.
 * @param {Function} reset - Function to reset component states when navigating away or reloading.
 * @param {Function} resetNotifications - Function to clear new notifications from the state.
 * @param {Object} permissions - Object containing permissions assigned to the current user.
 * @param {Object} userDetails - Information about the currently logged-in user.
 * @param {Object[]} notificationList - Array containing the list of notifications.
 * @param {number} newNotifications - Count of new notifications.
 */
const ResponsiveDrawer = ({
    allNotes,
    title,
    subtitle,
    children,
    userLogout,
    notesSave,
    fetchNotes,
    deleteNotes,
    showResultButtons,
    reset,
    resetNotifications,
    permissions,
    userDetails,
    notificationList,
    newNotifications,
}) => {
    const useStyles = makeStyles((theme) => ResponsiveDrawerStyle(theme));
    const classes = useStyles();
    const theme = useTheme();
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [openNotes, setOpenNotes] = React.useState(false);
    const [notes, setNotes] = React.useState("");
    const [openAllNotes, setAllNotes] = React.useState(false);
    const [viewAll, setViewAll] = React.useState(false);
    // eslint-disable-next-line no-unused-vars
    const [controlledPosition, setControlledPosition] = React.useState({
        x: 0,
        y: 0,
    });
    // eslint-disable-next-line no-unused-vars
    const [createNoteControlledPosition, setCreateNoteControlledPosition] = React.useState({
        x: 0,
        y: 0,
    });
    const [editNote, setEditNote] = React.useState("");
    const [showSims, setShowSims] = React.useState(false);
    const [showAdmin, setShowAdmin] = React.useState(false);
    const [showNotes, setShowNotes] = React.useState(false);
    const [createNotes, setCreateNotes] = React.useState(false);
    const [openNotifications, setOpenNotifications] = React.useState(false);
    const [showBtn, setBtn] = React.useState(false);
    const [showHelpdeskMenu, setShowHelpdeskMenu] = React.useState(false);
    const [hasNewNotifications, setHasNewNotifications] = React.useState(0);
    const [notifications, setNotifications] = React.useState([]);
    const [brandingLogo, setBrandingLogo] = React.useState(iLearnLogo);
    const container = document?.body || undefined;
    const containerRef = React.useRef();

    const notesPopperRef = React.useRef(null);

    // changes page title
    useEffect(() => {
        document.title = `iLearnCC | ${title}` || "";

        fetchBranding().then((result) => {
            if (result && result.success && result.brandingLogo) {
                setBrandingLogo(result.brandingLogo);
            }
        });
    }, [title]);

    useEffect(() => {
        setOpenNotes(false);
        setNotes("");
        setControlledPosition({ x: 0, y: 0 });
        setAllNotes(false);
        setViewAll(false);
        setCreateNoteControlledPosition({ x: 0, y: 0 });
        setEditNote("");
    }, [title, subtitle]);

    useEffect(
        () => {
            setHasNewNotifications(newNotifications);
        },
        [newNotifications]
    );

    useEffect(
        () => {
            setNotifications(notificationList ?? []);
        },
        [notificationList, setNotifications]
    );

    useEffect(
        () => {
            if (permissionCheck(DashboardPermissions.my_notepad.view.task, DashboardPermissions.my_notepad.view.type)) {
                fetchNotes();
            }
        },
        [fetchNotes, permissions]
    );

    useEffect(
        () => {
            reset();
        },
        [reset]
    );

    useEffect(
        () => {
            let canManageSims = (
                permissionCheck(SimsPermissions.sim_list.create.task, SimsPermissions.sim_list.create.type) ||
                permissionCheck(SimsPermissions.sim_list.update.task, SimsPermissions.sim_list.update.type) ||
                permissionCheck(SimsPermissions.sim_list.delete.task, SimsPermissions.sim_list.delete.type) ||
                permissionCheck(SimsPermissions.sim_list.view.task, SimsPermissions.sim_list.view.type)
            ) && (userDetails.role.toLowerCase() !== 'agent');
            setShowSims(canManageSims);
        },
        [setShowSims, permissions, userDetails]
    );

    useEffect(
        () => {
            let canAdmin = (
                permissionCheck(AdminPermissions.employee_list.create.task, AdminPermissions.employee_list.create.type) ||
                permissionCheck(AdminPermissions.employee_list.update.task, AdminPermissions.employee_list.update.type) ||
                permissionCheck(AdminPermissions.employee_list.delete.task, AdminPermissions.employee_list.delete.type) ||
                permissionCheck(AdminPermissions.employee_list.view.task, AdminPermissions.employee_list.view.type) ||
                permissionCheck(AdminPermissions.groups.create.task, AdminPermissions.groups.create.type) ||
                permissionCheck(AdminPermissions.groups.update.task, AdminPermissions.groups.update.type) ||
                permissionCheck(AdminPermissions.groups.delete.task, AdminPermissions.groups.delete.type) ||
                permissionCheck(AdminPermissions.groups.view.task, AdminPermissions.groups.view.type) ||
                permissionCheck(AdminPermissions.permissions.create.task, AdminPermissions.permissions.create.type) ||
                permissionCheck(AdminPermissions.permissions.update.task, AdminPermissions.permissions.update.type) ||
                permissionCheck(AdminPermissions.permissions.view.task, AdminPermissions.permissions.view.type) ||
                permissionCheck(AdminPermissions.roles.create.task, AdminPermissions.roles.create.type) ||
                permissionCheck(AdminPermissions.roles.update.task, AdminPermissions.roles.update.type) ||
                permissionCheck(AdminPermissions.roles.delete.task, AdminPermissions.roles.delete.type) ||
                permissionCheck(AdminPermissions.roles.view.task, AdminPermissions.roles.view.type)
            ) && (userDetails.role.toLowerCase() !== 'agent');
            setShowAdmin(canAdmin);
        },
        [setShowAdmin, permissions, userDetails]
    );

    useEffect(
        () => {
            let canViewNotes = permissionCheck(
                DashboardPermissions.my_notepad.view.task,
                DashboardPermissions.my_notepad.view.type,
            );
            setShowNotes(canViewNotes);
        },
        [setShowNotes, permissions]
    );

    useEffect(
        () => {
            let canCreateNotes = permissionCheck(
                DashboardPermissions.my_notepad.create.task,
                DashboardPermissions.my_notepad.create.type,
            );
            setCreateNotes(canCreateNotes);
        },
        [setCreateNotes, permissions]
    );

    useEffect(
        () => {
            let canShowHelpdeskMenu = permissionCheck(
                HelpdeskPermissions.tickets.view.task,
                HelpdeskPermissions.tickets.view.type
            );
            // Hide HelpDesk until further notice
            setShowHelpdeskMenu(canShowHelpdeskMenu);
        },
        [setShowHelpdeskMenu],
    );

    /**
     * Toggles the state of the mobile navigation drawer.
     *
     * This function is used to handle the open and close state
     * of a mobile drawer by updating the corresponding state variable.
     * It inversely sets the value of `mobileOpen` to either true or false.
     */
    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    /**
     * Toggles the state of a button.
     *
     * This function sets the new state of the button by inverting
     * the current value of `showBtn` and updating it using the `setBtn` function.
     *
     * @function
     */
    const handleBtnToggle = () => {
        setBtn(!showBtn);
    };

    /**
     * Handles the interaction with the notes feature.
     *
     * This function sets the anchor element for the notes menu
     * and opens the notes interface.
     *
     * @param {Object} evt - The event object triggered by the user interaction.
     * @param {Object} evt.currentTarget - The current target element of the event.
     * @param {Object} evt.currentTarget.parentNode - The parent node of the current target element.
     */
    const handleNotes = (/* evt */) => {
        setOpenNotes(true);
    };

    /**
     * A function to handle the action of displaying all notes.
     * It sets the state indicating that all notes are being shown and anchors the notes menu to the current target element.
     *
     * @function
     * @param {Event} e - The event object triggered by the user interaction.
     * @returns {void}
     */
    const handleAllNotes = (/* e */) => {
        setAllNotes(!openAllNotes);
    };

    /**
     * Handles the closing action for notes.
     *
     * This function performs the following actions:
     * - Closes the notes interface by updating the state to false.
     * - Clears the current note content by resetting the notes state to an empty string.
     * - Resets the position of the note creation interface to its initial coordinates (x: 0, y: 0).
     */
    const handleClose = () => {
        setOpenNotes(false);
        setNotes("");
    };

    /**
     * Handles the closure of the Notes interface.
     *
     * This function performs the following actions:
     * - Disables or hides all notes by setting their state to `false`.
     * - Closes the notifications section by disabling its state.
     * - Disables the "View All" state for notes or notifications.
     * - Resets the position of a controlled UI element to its default coordinates (x: 0, y: 0).
     * - Clears the current edit note by resetting it to an empty string.
     */
    const handleNotesClose = () => {
        setAllNotes(false);
        setOpenNotifications(false);
        setViewAll(false);
        setEditNote("");
    };

    /**
     * A function that saves user notes along with a corresponding SIM ID to the storage or system.
     *
     * The function retrieves the SIM ID from localStorage if available and packages it together with the notes to create a data object.
     * It then performs the following actions:
     * - Closes the notes UI or popup
     * - Resets the notes field to an empty string
     * - Saves the note data using the `notesSave` method
     * - Fetches updated notes from storage or backend to ensure changes are reflected
     *
     * This function assumes the presence of `setOpenNotes`, `setNotes`, `notesSave`, and `fetchNotes` functions in the same context.
     */
    const saveNotes = () => {
        const data = localStorage.getItem("sims_id") ? {
            notes,
            sim_id: localStorage.getItem("sims_id")
        } : {notes};
        setOpenNotes(false);
        setNotes("");
        notesSave(data);
        fetchNotes();
    };

    /**
     * Deletes a specific note by its identifier and refreshes the notes list.
     *
     * @function deleteNote
     * @param {number|string} id - The unique identifier of the note to be deleted.
     */
    const deleteNote = (id) => {
        deleteNotes(id);
        fetchNotes();
    };

    /**
     * Handles the drag event of a draggable element and updates its position.
     *
     * @function handleDrag
     * @param {Object} e - The event object associated with the drag action.
     * @param {Object} position - An object containing the new position of the element.
     * @param {number} position.x - The x-coordinate of the element's new position.
     * @param {number} position.y - The y-coordinate of the element's new position.
     * @returns {void}
     */
    const handleDrag = (e /* , position */) => {
        setControlledPosition({x: e.screenX, y: e.screenY});
    };

    /**
     * Handles the drag end event and updates the controlled position based on the drag event.
     *
     * @function
     * @param {Object} e - The event object associated with the drag end event.
     * @param {Object} position - The final position of the dragged element.
     * @param {number} position.x - The x-coordinate of the dragged element's final position.
     * @param {number} position.y - The y-coordinate of the dragged element's final position.
     */
    const handleDragEnd = (e/* , position */) => {
        setControlledPosition({x: e.screenX, y: e.screenY});
    };

    /**
     * Handles the creation of a note by dragging.
     *
     * @function handleDragCreateNote
     * @param {Object} e - The event object from the drag interaction.
     * @param {Object} position - The position of the mouse or pointer during the drag event.
     * @param {number} position.x - The x-coordinate of the position where the note is being created.
     * @param {number} position.y - The y-coordinate of the position where the note is being created.
     * @description Updates the controlled position of note creation, using the provided x and y coordinates from the position.
     */
    const handleDragCreateNote = (e/* , position */) => {
        setControlledPosition({x: e.screenX, y: e.screenY});
    };

    /**
     * Handles the end event of a drag operation for creating a note.
     *
     * @param {Object} e - The event object associated with the drag action.
     * @param {Object} position - The current position of the dragged element.
     * @param {number} position.x - The x-coordinate of the element's position.
     * @param {number} position.y - The y-coordinate of the element's position.
     */
    const handleDragCreateNoteEnd = (e /* , position */) => {
        setCreateNoteControlledPosition({x: e.screenX, y: e.screenY});
    };

    /**
     * Updates the state of the note being edited based on user input.
     *
     * @function
     * @param {Object} e - The event object triggered by the input change event.
     * @param {string} e.target.name - The name attribute of the input element, used as the identifier of the note.
     * @param {string} e.target.value - The current value of the input element, representing the updated note text.
     */
    const handleChangeNotes = (e) => {
        const id = e.target.name;
        setEditNote({
            id: id,
            note: e.target.value,
        });
    };

    /**
     * Handles the editing of a note.
     *
     * This function executes the process of updating an existing note by first checking
     * if a note ID is specified. If a valid note ID is present, it creates a data object
     * containing the ID and updated note content. The data object is then passed to the
     * `notesSave` function to persist the changes. Finally, the `fetchNotes` function is
     * called to refresh the list of notes.
     *
     * Preconditions:
     * - `editNote` should be an object containing an `id` and `note` property.
     * - A valid `id` is required for the function to proceed.
     *
     * Side Effects:
     * - Calls `notesSave` to update the note.
     * - Calls `fetchNotes` to retrieve and update the list of notes.
     */
    const handleEditNote = () => {
        if (!editNote.id) {
            return
        }
        const data = {
            id: editNote.id,
            notes: editNote.note,
        };
        notesSave(data);
        fetchNotes();
    };

    let data = viewAll ?
        [...allNotes] :
        allNotes?.slice(0, 4);

    /**
     * Displays a list of notification elements or a message indicating no notifications.
     * It iterates through the notifications array and creates JSX elements for each notification,
     * detailing the message, creation date, and sender's name. If no notifications exist, a fallback
     * message is displayed.
     *
     * @return {Array|JSX.Element} Returns an array of JSX elements representing notifications if they exist.
     *         If no notifications are available, returns a single JSX element indicating there are no notifications.
     */
    function showNotifications() {
        if (notifications && notifications.length) {
            return notifications.map((notify) => {
                return (
                    <div
                        key={notify._id}
                        className={notify.isNew ? classes.cardBgNew : classes.cardBg}
                    >
                        <div style={{ margin: "5px 0px 5px 8px" }}>
                            <Typography variant="subtitle1" style={{ fontSize: 12 }}>
                                {notify.message}
                            </Typography>
                            <Typography
                                variant="caption"
                                className={classes.typo}
                            >
                                Added On - {(new Date(notify.created_date)).toDateString()}
                            </Typography><br/>
                            <Typography
                                variant="caption"
                                className={classes.typo}
                            >
                                Sender Name - {notify.created_by}
                            </Typography>
                        </div>
                    </div>
                );
            });
        }

        return (
            <div
                key={0}
                className={classes.cardBg}
            >
                <div style={{ margin: "5px 0px 5px 8px" }}>
                    <Typography variant="subtitle1" style={{ fontSize: 12 }}>
                        There are no Notifications
                    </Typography>
                </div>
            </div>
        );
    }

    /**
     * Handles the notification logic.
     *
     * This asynchronous function performs actions related to resetting notifications
     * and updating the state of the notification system. It maps through the list of
     * notifications to reset their status and sets the visibility of notifications.
     *
     * The function ensures that notifications are appropriately managed and updated
     * within the application's context.
     *
     * Note:
     * - The `fetchNotification` function call is commented out, and might require
     *   enabling depending on specific implementation requirements.
     */
    const handleNotifications = async() => {
        resetNotifications(notificationList.map(notif => notif._id));
        setOpenNotifications(true);
    };

    /**
     * Downloads user notes as an Excel file.
     *
     * This function initiates the download of user notes by calling
     * the `downloadExcelNotes` function, which fetches the data.
     * Once the data is received, it is converted into a Blob object with
     * the appropriate MIME type for an Excel file, and then triggers
     * a download with a predefined file name 'User_Notes.xlsx'.
     *
     * Prerequisite:
     * - Make sure the `downloadExcelNotes` function is defined and returns
     *   the downloadable data.
     * - Ensure that FileSaver is available in the project to handle file-saving operations.
     */
    const downloadNotes = () => {
        downloadExcelNotes().then((res) => {
            const fileName = 'User_Notes';
            const blob = new Blob([res.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
        })
    };

    /**
     * Represents a navigational drawer component in a user interface.
     *
     * This object contains multiple menu options that navigate the user to different
     * sections of the application, including "DASHBOARD", "SIMS", "ADMIN", "HELPDESK", and "SETTINGS".
     * Each menu option may include an associated icon and is displayed or hidden based on certain
     * conditions such as application state, user title, and permissions.
     *
     * The drawer also supports the ability to highlight the currently active menu item
     * through the `selected` property, dynamically displays dividers between certain menu categories,
     * and is styled with classes and themed through a `ThemeProvider`.
     *
     * The menu items permit conditional rendering based on variables such as:
     * - `showSims`: Displays the "SIMS" menu item if true.
     * - `showAdmin`: Displays the "ADMIN" menu item if true.
     * - `showHelpdeskMenu`: Displays the "HELPDESK" menu item if true.
     *
     * Properties and behaviors:
     * - Uses a `ThemeProvider` for applying specific theme colors.
     * - Applies `selected` styling to the currently active menu item.
     * - Dynamically renders visual dividers between menu sections.
     * - Supports anchored navigation using `StyledLink` components to direct users to specified routes.
     * - Custom icons and text provided for each menu item.
     * - Implements conditional rendering for various menu items to configure the available options.
     */
    const drawer = (
        <ThemeProvider theme={menuTheme}>
            <div className={classes.toolbar} />
            <List style={{display: 'flex', flexDirection: 'column', flex: '1'}}>
                <StyledLink to="/dashboard">
                    <ListItem
                        variant="contained"
                        button
                        key="dashboard"
                        className={classes.menuContainer}
                        selected={title === "DASHBOARD"}
                    >
                        <ListItemIcon>
                            <DashboardIcon className={classes.menuIcon} />
                        </ListItemIcon>
                        <div className={classes.menuText}>DASHBOARD</div>
                    </ListItem>
                </StyledLink>
                {title !== "DASHBOARD" && title !== "SIMS" && (
                    <Divider variant="middle" />
                )}
                {showSims ?
                    <StyledLink to="/sims">
                        <ListItem
                            button
                            key="sims"
                            className={classes.menuContainer}
                            selected={title === "SIMS"}
                        >
                            <ListItemIcon>
                                <PlayIcon className={classes.menuIcon} />
                            </ListItemIcon>
                            <div className={classes.menuText}>SIMS</div>
                        </ListItem>
                    </StyledLink> : null}
                {title !== "SIMS" && title !== "ADMIN" && <Divider variant="middle" />}
                {showAdmin ?
                    <StyledLink to="/admin">
                        <ListItem
                            button
                            key="admin"
                            className={classes.menuContainer}
                            selected={title === "ADMIN"}
                        >
                            <ListItemIcon>
                                <img
                                    src={AdminIcon}
                                    height={23}
                                    width={23}
                                    alt="Admin"
                                    className={classes.adminIcon}
                                />
                            </ListItemIcon>
                            <div className={classes.menuText}>ADMIN</div>
                        </ListItem>
                    </StyledLink> : null}
                {title !== "ADMIN" && title !== "ACCOUNT" && (
                    <Divider variant="middle" />
                )}

                {title !== "ACCOUNT" && title !== "TOOLS" && (
                    <Divider variant="middle" />
                )}

                {title !== "TOOLS" && title !== "HELPDESK" && (
                    <Divider variant="middle" />
                )}
                {showHelpdeskMenu && (
                    <>
                        <StyledLink to="/helpdesk">
                            <ListItem
                                button
                                key="helpdesk"
                                className={classes.menuContainer}
                                selected={title === "HELPDESK"}
                            >
                                <ListItemIcon>
                                    {/* TODO - Add pill with count of help requests (and ready for review assignments?)*/}
                                    <LiveHelpIcon className={classes.menuIcon} />
                                </ListItemIcon>
                                <div className={classes.menuText}>HELPDESK</div>
                            </ListItem>
                        </StyledLink>
                        {title !== "HELPDESK" && title !== "SETTINGS" && (
                    <Divider variant="middle" />
                )}
                    </>
                )}
                <StyledLink to="/settings">
                    <ListItem
                        button
                        key="settings"
                        className={classes.menuContainer}
                        selected={title === "SETTINGS"}
                    >
                        <ListItemIcon>
                            <SettingsIcon className={classes.menuIcon} />
                        </ListItemIcon>
                        <div className={classes.menuText}>SETTINGS</div>
                    </ListItem>
                </StyledLink>
                {permissionCheck(HelpdeskPermissions.tickets.create.task, HelpdeskPermissions.tickets.create.type) && (
                    <ListItem button id="help_request"
                              className={classes.helpIcon}
                              style={{marginTop: 'auto', marginBottom: '1em', display: 'inline-flex', flexDirection: 'column'}}
                    >
                        <Tooltip title="Request help from a trainer">
                            <ListItemIcon>
                                <HelpRequestButton className={classes.menuIcon} classes={classes}/>
                            </ListItemIcon>
                        </Tooltip>
                    </ListItem>
                )}
            </List>
        </ThemeProvider>
    );

    return (
        <div ref={containerRef}
             className={classes.root}>
            <CssBaseline />
            <AlertPopup />
            <AppBar position="fixed" className={classes.appBar} color="default">
                <Toolbar
                    disableGutters
                    className={classes.customToolbar}
                    variant="dense"
                >
                    <div className={classes.logo}>
                        <img
                            src={brandingLogo}
                            width={48}
                            style={{height: 'auto'}}
                            alt='logo'
                        >
                        </img>
                    </div>
                    <Divider
                        orientation="vertical"
                        flexItem
                        className={classes.divider}
                    />
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        className={classes.menuButton}
                        size="small"
                    >
                        <MenuIcon />
                    </IconButton>
                    <Box
                        className={classes.box}
                        display="flex"
                        ml={3}
                        alignItems="center"
                    >
                        <Typography
                            variant="h6"
                            color={subtitle ? "primary" : "textPrimary"}
                            className={classes.title}
                        >
                            {title}
                        </Typography>
                        {subtitle && (
                            <>
                                <Box display="flex" alignItems="center">
                                    <KeyboardArrowRightIcon fontSize="small" />
                                </Box>
                                <Typography vairant="h6" className={classes.title}>
                                    {subtitle} {showResultButtons ? " > RESULTS " : ""}
                                </Typography>
                            </>
                        )}
                        {subtitle === "TAKE SIM" && !showResultButtons &&
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                style={{marginLeft: 7}}
                                onClick={handleBtnToggle}
                                className={classes.menuButton}
                                size="small"
                            >
                                <MenuIcon />
                            </IconButton>}
                    </Box>
                    {subtitle === "CREATE SIM" && <NewSimMenu />}
                    {subtitle === "TAKE SIM" && !showResultButtons && !showBtn &&
                        <DownloadButtons />
                    }
                    {subtitle === "TAKE SIM" && !showResultButtons && showBtn &&
                        <DownloadButtons showBtn={showBtn} handleBtnToggle={handleBtnToggle}/>
                    }
                    {subtitle === "TAKE SIM" && showResultButtons && <ResultScreen />}

                    <List className={classes.actionContainer}>
                        <ListItem>
                            {
                                <NotificationsIcon onClick={handleNotifications} className={classes.notificationIcon} />
                            }
                            { hasNewNotifications
                                ? (
                                    <Badge
                                        className={classes.badge}
                                        max={999}
                                        showZero
                                        badgeContent={newNotifications}
                                        overlap={'circular'}
                                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                    />
                                )
                                : '' }
                        </ListItem>
                        {createNotes ?
                            <ListItem button key="add note" disableGutters>
                                <img
                                    src={Notes}
                                    height={30}
                                    width={30}
                                    alt="notes"
                                    onClick={handleNotes}
                                />
                                <Draggable
                                    handle="#drag1"
                                    onDrag={handleDragCreateNote}
                                    onStop={handleDragCreateNoteEnd}
                                    defaultPosition={{x: 0, y: 0}}
                                    position={null}
                                    grid={[5, 5]}
                                    scale={1}
                                >
                                    <Popper
                                        open={openNotes}
                                        style={{overflowY: 'auto'}}
                                        className={classes.notesPopover}
                                        anchorEl={notesPopperRef.current}
                                        placement="left"
                                        disablePortal={false}
                                        modifiers={{
                                            flip: {
                                                enabled: true,
                                            },
                                            preventOverflow: {
                                                enabled: true,
                                                boundariesElement: "scrollParent",
                                            },
                                        }}
                                    >
                                        <div style={{ padding: "0.5rem", width: "293px" }}>
                                            <div className={classes.popupHead}>
                                                <Tooltip title="Close">
                                                    <IconButton
                                                        style={{ padding: "1px" }}
                                                        onClick={handleClose}
                                                    >
                                                        <CloseIcon
                                                            style={{
                                                                backgroundColor: "#741C39",
                                                                float: "left",
                                                                color: "white",
                                                                padding: 5,
                                                                borderRadius: "1px",
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                                <Typography
                                                    style={{
                                                        display: "inline-block",
                                                        padding: "4px 12px",
                                                        color: "white",
                                                        fontSize: "0.8rem",
                                                    }}
                                                >
                                                    New Class Note
                                                </Typography>
                                                <IconButton
                                                    id="drag1"
                                                    style={{ float: "right", padding: 5 }}
                                                >
                                                    <img
                                                        src={DragIcon}
                                                        width={17}
                                                        height={17}
                                                        alt="drag icon"
                                                    />
                                                </IconButton>
                                            </div>
                                            <TextareaAutosize
                                                inputprops={{
                                                    disableUnderline: true,
                                                    style: { fontSize: 12, paddingLeft: 8 },
                                                }}
                                                placeholder="Type your note here"
                                                style={{
                                                    backgroundColor: "#F1F1F1",
                                                    marginTop: "11px",
                                                    borderRadius: "4px",
                                                }}
                                                className={classes.notes}
                                                minRows={8}
                                                value={notes}
                                                onChange={(e) => setNotes(e.target.value)}
                                                data-enable-grammarly="false"
                                            />
                                            <Button
                                                size="small"
                                                style={{
                                                    fontWeight: 600,
                                                    padding: "3px 11px",
                                                    backgroundColor: "#F8DEE7",
                                                    float: "right",
                                                    color: "#A55B73",
                                                    margin: "8px 0px",
                                                }}
                                                onClick={saveNotes}
                                            >
                                                <CheckIcon
                                                    style={{ width: "17px", marginRight: "5px" }}
                                                />
                                                Save
                                            </Button>
                                        </div>
                                    </Popper>
                                </Draggable>
                            </ListItem>
                            : ''
                        }
                        <ListItem button key="see notes">
                            <Draggable
                                handle="#drag2"
                                onDrag={handleDrag}
                                onStop={handleDragEnd}
                                defaultPosition={{x: 0, y: 0}}
                                position={null}
                                grid={[5, 5]}
                                scale={1}
                            >
                                <Popper
                                    open={openAllNotes}
                                    className= {classes.notesPopover}
                                    anchorEl={notesPopperRef.current}
                                    placement="left"
                                    disablePortal={false}
                                    modifiers={{
                                        flip: {
                                            enabled: true,
                                        },
                                        preventOverflow: {
                                            enabled: true,
                                            boundariesElement: "scrollParent",
                                        },
                                    }}
                                >
                                    <div style={{ padding: "0.5rem", width: "293px" }}>
                                        <div className={classes.popupHead}>
                                            <Tooltip title="Close">
                                                <IconButton
                                                    style={{ padding: "1px" }}
                                                    onClick={handleNotesClose}
                                                >
                                                    <CloseIcon
                                                        style={{
                                                            backgroundColor: "#741C39",
                                                            float: "left",
                                                            color: "white",
                                                            padding: 5,
                                                            borderRadius: "1px",
                                                        }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                            <Typography
                                                style={{
                                                    display: "inline-block",
                                                    padding: "4px 12px",
                                                    color: "white",
                                                    fontSize: "0.8rem",
                                                }}
                                            >
                                                Class Notes ({allNotes?.length})
                                            </Typography>
                                            <IconButton
                                                id="drag2"
                                                style={{ float: "right", padding: 5, cursor: 'grabbing' }}
                                            >
                                                <img
                                                    src={DragIcon}
                                                    width={17}
                                                    height={17}
                                                    alt="drag icon"
                                                />
                                            </IconButton>
                                            {
                                                (userDetails.role.toLowerCase() !== 'agent') &&
                                                <Tooltip title="Download Notes">
                                                    <GetAppIcon
                                                        className={classes.iconDownload}
                                                        onClick={downloadNotes}
                                                    />
                                                </Tooltip>
                                            }

                                        </div>
                                        {data?.map((note) => {
                                            return (
                                                <div
                                                    key={note._id}
                                                    style={{
                                                        backgroundColor: "#F1F1F1",
                                                        marginTop: "11px",
                                                        borderRadius: "4px",
                                                        padding: "5px",
                                                    }}
                                                >
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            style={{ padding: "0px", float: "right" }}
                                                            onClick={() => deleteNote(note._id)}
                                                        >
                                                            <CloseIcon
                                                                style={{
                                                                    borderRadius: "3px",
                                                                    width: "15px",
                                                                    height: "15px",
                                                                    padding: "2px",
                                                                    backgroundColor: "white",
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Save">
                                                        <IconButton
                                                            style={{
                                                                padding: "0px",
                                                                float: "right",
                                                                marginRight: 8,
                                                            }}
                                                            onClick={handleEditNote}
                                                        >
                                                            <CheckIcon
                                                                style={{
                                                                    borderRadius: "3px",
                                                                    width: "15px",
                                                                    height: "15px",
                                                                    padding: "2px",
                                                                    backgroundColor: "white",
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <div style={{ margin: "5px 0px 5px 8px" }}>
                                                        {/* <Typography variant="subtitle1" style={{ fontSize: 12 }}>
                                  {note.notes}
                                </Typography> */}
                                                        <TextField
                                                            inputprops={{
                                                                disableUnderline: true,
                                                                style: { fontSize: 12 },
                                                            }}
                                                            fullWidth
                                                            multiline
                                                            // minRows={5}
                                                            name={note._id}
                                                            value={
                                                                editNote?.id === note._id
                                                                    ? editNote.note
                                                                    : note.notes
                                                            }
                                                            onChange={(e) => handleChangeNotes(e)}
                                                            style={{
                                                                backgroundColor: "#F1F1F1",
                                                                borderRadius: "4px",
                                                            }}
                                                            // placeholder="Type your note here"
                                                        />
                                                        <Tooltip title={ note?.sim_name ?? 'None'}>
                                                            <Typography
                                                                variant="caption"
                                                                style={{ fontSize: 12, color: "#AEADAE" }}
                                                            >
                                                                {note?.sim_name ?
                                                                    (
                                                                        note.sim_name.length > 22 ?
                                                                            `Sim Name - ${note.sim_name.slice(0, 22)}  ...` :
                                                                            `Sim Name - ${note.sim_name}`
                                                                    ) :
                                                                    ''
                                                                }
                                                            </Typography>
                                                        </Tooltip>

                                                        <br />
                                                        <Typography
                                                            variant="caption"
                                                            style={{ fontSize: 12, color: "#AEADAE" }}
                                                        >
                                                            Added On - {(new Date(note.createdAt)).toDateString()}
                                                        </Typography>
                                                        <br />
                                                        <Typography
                                                            variant="caption"
                                                            style={{ fontSize: 12, color: "#AEADAE" }}
                                                        >
                                                            Last Updated - {(new Date(note.updatedAt)).toDateString()}
                                                        </Typography>
                                                        <br />
                                                        <Typography
                                                            variant="caption"
                                                            style={{ fontSize: 12, color: "#AEADAE" }}
                                                        >
                                                            Created By - {note.user}
                                                        </Typography>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {allNotes.length > 4 && (
                                            <Button
                                                variant="text"
                                                size="small"
                                                style={{
                                                    fontWeight: 600,
                                                    padding: "3px 11px",
                                                    color: "#A55B73",
                                                    margin: "5px 99px",
                                                }}
                                                onClick={() => setViewAll(true)}
                                            >
                                                View All
                                            </Button>
                                        )}
                                    </div>
                                </Popper>
                            </Draggable>
                            {showNotes ?
                                <ButtonGroup
                                    variant="contained"
                                    aria-label="split button"
                                    size="small"
                                    disableElevation
                                >
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        className={classes.button}
                                    >
                                        NOTES&nbsp;({allNotes?.length})
                                    </Button>
                                    <Button
                                        variant="contained"
                                        aria-label="NOTES (`{$allNotes?.length}`)"
                                        className={classes.expand}
                                        onClick={handleAllNotes}
                                    >
                                        {openAllNotes ? <ExpandLess /> : <ExpandMore />}
                                    </Button>
                                </ButtonGroup>
                                : ''
                            }
                        </ListItem>

                        <Divider
                            orientation="vertical"
                            flexItem
                            className={classes.divider}
                        />
                        <Tooltip title="SignOut" placement="top">
                            <ListItem button onClick={() => {
                                userLogout(); reset();
                            }}
                                      key="logout" size="small">
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size="small"
                                    className={classes.logOut}
                                >
                                    SIGN OUT
                                </Button>
                            </ListItem>
                        </Tooltip>
                    </List>
                </Toolbar>
                <Popover
                    open={openNotifications}
                    container={containerRef.current}
                    anchorReference="anchorPosition"
                    anchorPosition={{ top: 47, left: 1103 }}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                >
                    <div className={classes.notifyDiv}>
                        <div className={classes.popupHead}>
                            <Tooltip title="Close">
                                <IconButton
                                    style={{ padding: "1px" }}
                                    onClick={handleNotesClose}
                                >
                                    <CloseIcon className={classes.closeIcon}/>
                                </IconButton>
                            </Tooltip>
                            <Typography className={classes.heading}>
                                Notifications
                            </Typography>
                        </div>
                        {showNotifications()}
                    </div>
                </Popover>
            </AppBar>
            <nav className={classes.drawer} aria-label="menu nav">
                {/* The implementation can be swapped with js to avoid SEO duplication of StyledLinks. */}
                <Hidden smUp implementation="css">
                    <Drawer
                        container={container}
                        variant="temporary"
                        anchor={theme.direction === "rtl" ? "right" : "left"}
                        open={mobileOpen}
                        onClose={handleDrawerToggle}
                        classes={{
                            paper: classes.drawerPaper,
                        }}
                        ModalProps={{
                            keepMounted: true, // Better open performance on mobile.
                        }}
                    >
                        {drawer}
                    </Drawer>
                </Hidden>
                <Hidden xsDown implementation="css">
                    <Drawer
                        classes={{
                            paper: classes.drawerPaper,
                        }}
                        variant="permanent"
                        open
                    >
                        {drawer}
                    </Drawer>
                </Hidden>
            </nav>
            <main className={classes.content}>
                <div
                    className={classes.customToolbar}
                    style={{ marginBottom: "20px" }}
                />
                {children}
            </main>
        </div>
    );
};

/**
 * PropTypes for the ResponsiveDrawer component.
 *
 * @prop {boolean} mobileOpen - Indicates whether the mobile drawer is open.
 * @prop {func} handleDrawerToggle - Callback function to handle the toggle state of the drawer.
 * @prop {node} drawer - Content or component rendered inside the drawer.
 * @prop {node} toolbar - Content or component rendered inside the toolbar area.
 * @prop {node} children - The main content of the page displayed alongside the drawer.
 */
ResponsiveDrawer.propTypes = {
    window: PropTypes.func,
    notesSave: PropTypes.func,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    userLogout: PropTypes.func,
    fetchNotes: PropTypes.func,
    deleteNotes: PropTypes.func,
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
    allNotes: PropTypes.array,
    showResultButtons: PropTypes.bool,
    permissions: PropTypes.array,
    userDetails: PropTypes.object,
    newNotifications: PropTypes.number,
    reset: PropTypes.func,
    resetNotifications: PropTypes.func,
    notificationList: PropTypes.array,
};

/**
 * Maps the relevant state properties to the props of a component.
 *
 * @param {Object} state - The global state object from the Redux store.
 * @returns {Object} An object containing the mapped state properties:
 *  - `showResultButtons`: Indicates whether to display result buttons, taken from `state.sim.showResultButtons`.
 *  - `allNotes`: A collection of all notes, sourced from `state.sim.notes`.
 *  - `permissions`: The user's permissions, retrieved from `state.login.permissions`.
 *  - `userDetails`: Information about the logged-in user, obtained from `state.login.user`.
 *  - `notificationList`: List of user notifications, pulled from `state.login.notifications`.
 *  - `newNotifications`: List of new notifications, derived from `state.login.newNotifications`.
 */
const mapStateToProps = (state) => ({
    showResultButtons: state.sim.showResultButtons,
    allNotes: state.sim.notes,
    permissions: state.login.permissions,
    userDetails: state.login.user,
    notificationList: state.login.notifications,
    newNotifications: state.login.newNotifications,
});

/**
 * Object containing functions that dispatch actions to the Redux store.
 *
 * @property {Function} userLogout - Dispatches an action to log the user out of the application.
 * @property {Function} notesSave - Dispatches an action to save notes to the state or backend.
 * @property {Function} fetchNotes - Dispatches an action to retrieve notes from the backend or state.
 * @property {Function} deleteNotes - Dispatches an action to delete notes from the state or backend.
 * @property {Function} reset - Dispatches an action to reset the state or clear specific data.
 * @property {Function} fetchNotification - Dispatches an action to fetch notifications.
 * @property {Function} resetNotification - Dispatches an action to reset a single notification.
 * @property {Function} resetNotifications - Dispatches an action to reset all notifications.
 */
const mapDispatchToProps = {
    userLogout,
    notesSave,
    fetchNotes,
    deleteNotes,
    reset,
    fetchNotification,
    resetNotification,
    resetNotifications,
};

export default connect(mapStateToProps, mapDispatchToProps)(ResponsiveDrawer);
